import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { OUTREACH_PITCH_KINDS, type OutreachPitchKind } from 'fym-shared/src/music24/outreach/dailyPitches';
import { TypedMemo } from '../../../common/types';
import { useMusic24Capabilities } from '../../hooks/useMusic24Capabilities';
import { useCampaignActions } from '../hooks/useCampaignActions';
import { useCampaignPool } from '../hooks/useCampaignPool';
import { useMailboxes } from '../hooks/useMailboxes';
import type { CampaignDetail } from '../outreachTypes';
import { FineTune } from './FineTune';
import { FirstWeek } from './FirstWeek';
import { KindRow } from './KindRow';
import { NeverContact } from './NeverContact';
import { PlanLadder } from './PlanLadder';
import { QuestionPanel } from './QuestionPanel';
import { RateCard } from './RateCard';
import { StepBar } from './StepBar';
import { NEXT_LABELS } from './builderSteps';
import { whoMessages as messages } from './whoMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for step 2; each part uses its own slice of it */
import styles from './Who.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface StepWhoProps {
  accountId: number;
  campaign: CampaignDetail;
  readOnly: boolean;
  onBack: () => void;
  onNext: () => void;
}

/**
 * Step 2 "Who Plug contacts" (design outreach-v2 c1, c2): the daily rate of the plan, the kinds of
 * people, the fine-tune from Your sound, "Never contact" and the first week. No list of people:
 * names show on the campaign page after the pitch (owner, 2026-10-23).
 */
const StepWhoComponent: React.FC<StepWhoProps> = ({ accountId, campaign, readOnly, onBack, onNext }) => {
  const intl = useIntl();
  const { plan } = useMusic24Capabilities();
  const { updateCampaign } = useCampaignActions(accountId);
  const { pool } = useCampaignPool(accountId, campaign.campaignUuid);
  const { mailboxes } = useMailboxes(accountId);
  const [kinds, setKinds] = useState<OutreachPitchKind[]>(campaign.kinds);
  const [failed, setFailed] = useState(false);

  const toggle = useCallback(
    (kind: OutreachPitchKind, on: boolean) => {
      const next = OUTREACH_PITCH_KINDS.filter((item) => (item === kind ? on : kinds.includes(item)));
      if (next.length === 0) {
        return;
      }
      setKinds(next);
      setFailed(false);
      updateCampaign({ campaignUuid: campaign.campaignUuid, kinds: next }).catch(() => {
        setKinds(kinds);
        setFailed(true);
      });
    },
    [campaign.campaignUuid, kinds, updateCampaign]
  );

  const rows = useMemo(
    () =>
      OUTREACH_PITCH_KINDS.map((kind) => {
        const on = kinds.includes(kind);
        return (
          <KindRow
            key={kind}
            kind={kind}
            on={on}
            count={pool === null ? null : pool.byKind[kind]}
            disabled={readOnly || (on && kinds.length === 1)}
            onToggle={toggle}
          />
        );
      }),
    [kinds, pool, readOnly, toggle]
  );
  const fitNow = useMemo(
    () => (pool === null ? campaign.poolTotal : kinds.reduce((sum, kind) => sum + pool.byKind[kind], 0)),
    [campaign.poolTotal, kinds, pool]
  );
  const mailbox = useMemo(
    () => mailboxes.find((row) => campaign.mailboxUuids.includes(row.mailboxUuid)) ?? null,
    [campaign.mailboxUuids, mailboxes]
  );

  return (
    <>
      <RateCard campaign={campaign} />
      <PlanLadder plan={plan} />
      <QuestionPanel
        title={intl.formatMessage(messages.kindsTitle)}
        hint={intl.formatMessage(messages.kindsHint)}
        flush
        testId="builder-step-who"
      >
        <div className={styles.kinds}>{rows}</div>
      </QuestionPanel>
      {failed && <p className="text-danger mb-0">{intl.formatMessage(messages.saveFailed)}</p>}
      <FineTune accountId={accountId} />
      <NeverContact accountId={accountId} readOnly={readOnly} />
      <FirstWeek campaign={campaign} mailbox={mailbox} />
      <StepBar
        onBack={onBack}
        summary={intl.formatMessage(messages.barSummary, {
          kinds: kinds.length,
          fit: intl.formatNumber(fitNow),
          rate: campaign.dailyRate,
        })}
        nextLabel={intl.formatMessage(NEXT_LABELS.message)}
        onNext={onNext}
      />
    </>
  );
};

export const StepWho = TypedMemo(StepWhoComponent);
